import React, { useState, useEffect } from 'react';
import { User, Pill, AlertTriangle, Shield, Edit, X, Save, Plus, Minus } from 'lucide-react';
import { patientService } from '../../../patient/services/patientService';
import { patientMedicalService } from '../../../../services/patientMedicalService';

const MedicalDetailsContent = ({ patientId }) => {
  // State management
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editablePatient, setEditablePatient] = useState(null);
  const [backupPatient, setBackupPatient] = useState(null);
  const [showBMITooltip, setShowBMITooltip] = useState(false);
  
  // Collapsible sections state
  const [isGeneralDetailsExpanded, setIsGeneralDetailsExpanded] = useState(true);
  const [isMedicationsExpanded, setIsMedicationsExpanded] = useState(false);
  const [isMedicalAllergiesExpanded, setIsMedicalAllergiesExpanded] = useState(false);
  const [isOtherAllergiesExpanded, setIsOtherAllergiesExpanded] = useState(false);

  // Real medical data from API
  const [medications, setMedications] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [medicalDataLoading, setMedicalDataLoading] = useState(false);

  // Editable medical data arrays (with full record objects)
  const [editableMedications, setEditableMedications] = useState([]);
  const [editableMedicalAllergies, setEditableMedicalAllergies] = useState([]);
  const [editableOtherAllergies, setEditableOtherAllergies] = useState([]);
  
  // Track original data for comparison during save
  const [originalMedications, setOriginalMedications] = useState([]);
  const [originalMedicalAllergies, setOriginalMedicalAllergies] = useState([]);
  const [originalOtherAllergies, setOriginalOtherAllergies] = useState([]);
  
  // Reference data for dropdowns
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [dosageOptions, setDosageOptions] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [allergyTypeOptions, setAllergyTypeOptions] = useState([]);
  const [allergySeverityOptions, setAllergySeverityOptions] = useState([]);

  // Input states for adding new tags
  const [medicationInput, setMedicationInput] = useState('');
  const [medicalAllergyInput, setMedicalAllergyInput] = useState('');
  const [otherAllergyInput, setOtherAllergyInput] = useState('');

  // Toast notification state
  const [toast, setToast] = useState(null);

  // Dummy medical data for demonstration
  const dummyMedications = [
    "Lisinopril 10mg daily",
    "Metformin 500mg twice daily", 
    "Atorvastatin 20mg nightly",
    "Aspirin 81mg daily"
  ];

  const dummyMedicalAllergies = [
    "Penicillin",
    "Sulfa drugs",
    "Latex",
    "Iodine contrast"
  ];

  const dummyOtherAllergies = [
    "Pollen",
    "Dust mites", 
    "Cat dander",
    "Shellfish"
  ];

  // Load patient data and medical data
  useEffect(() => {
    console.log('MedicalDetailsContent loading for patient:', patientId);
    loadPatientData();
    loadReferenceData();
  }, [patientId]);

  // Load reference data for dropdowns
  const loadReferenceData = async () => {
    try {
      const [medications, dosages, frequencies, allergyTypes, allergySeverities] = await Promise.all([
        patientMedicalService.getMedicationOptions(),
        patientMedicalService.getDosageOptions(), 
        patientMedicalService.getFrequencyOptions(),
        patientMedicalService.getAllergyTypeOptions(),
        patientMedicalService.getAllergySeverityOptions()
      ]);

      setMedicationOptions(medications);
      setDosageOptions(dosages);
      setFrequencyOptions(frequencies);
      setAllergyTypeOptions(allergyTypes);
      setAllergySeverityOptions(allergySeverities);

    } catch (error) {
      console.error('❌ Error loading reference data:', error);
      // Set fallback options
      setMedicationOptions(['Lisinopril', 'Metformin', 'Atorvastatin', 'Aspirin']);
      setDosageOptions(['10mg', '20mg', '50mg', '100mg', '500mg']);
      setFrequencyOptions(['Once daily', 'Twice daily', 'Three times daily']);
      setAllergyTypeOptions(['Drug', 'Food', 'Environmental', 'Other']);
      setAllergySeverityOptions(['Mild', 'Moderate', 'Severe', 'Life-threatening']);
    }
  };
    
  const loadPatientData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching real patient data for ID:', patientId);
      
      // Try to fetch real patient data using patient_id (e.g., "P000002")
      const patientData = await patientService.getPatientByPatientId(patientId);
      console.log('✅ Real patient data loaded:', patientData);
      
      setPatient(patientData);
      setEditablePatient({ ...patientData });
      
      // Fetch medical data when patient is loaded
      if (patientData.id) {
        await fetchMedicalData(patientData.id);
      }
      setLoading(false);
      
    } catch (error) {
      console.error('❌ Failed to load real patient data, using mock data:', error);
      loadMockPatientData();
    }
  };
  
  const loadMockPatientData = () => {
    const mockPatientData = {
      patient_id: patientId || 'P001',
      first_name: 'John',
      last_name: 'Smith',
      height: '175',
      weight: '70'
    };
    
    setPatient(mockPatientData);
    setEditablePatient({ ...mockPatientData });
    
    // Initialize editable arrays with dummy data
    setEditableMedications([...dummyMedications]);
    setEditableMedicalAllergies([...dummyMedicalAllergies]);
    setEditableOtherAllergies([...dummyOtherAllergies]);
    setLoading(false);
  };

  // Fetch medical data from API
  const fetchMedicalData = async (patientId) => {
    try {
      setMedicalDataLoading(true);
      console.log('🔍 Fetching medical data for patient ID:', patientId);

      // Fetch only ACTIVE medications and allergies for UI display
      const [medicationsData, allergiesData] = await Promise.all([
        patientMedicalService.getActiveMedications(patientId),
        patientMedicalService.getActiveAllergies(patientId)
      ]);

      console.log('💊 Medications data:', medicationsData);
      console.log('🚨 Allergies data:', allergiesData);

      setMedications(medicationsData);
      setAllergies(allergiesData);

      // Store original data for comparison during save
      setOriginalMedications([...medicationsData]);
      
      const drugAllergies = allergiesData.filter(allergy => allergy.allergy_type === 'Drug');
      const otherAllergies = allergiesData.filter(allergy => allergy.allergy_type !== 'Drug');
      
      setOriginalMedicalAllergies([...drugAllergies]);
      setOriginalOtherAllergies([...otherAllergies]);

      // Initialize editable arrays with real data (keep as objects for proper tracking)
      setEditableMedications([...medicationsData]);
      setEditableMedicalAllergies([...drugAllergies]);
      setEditableOtherAllergies([...otherAllergies]);

    } catch (error) {
      console.error('❌ Error fetching medical data:', error);
      console.log('🔄 Falling back to dummy data');
      
      // Clear all data if API fails
      setMedications([]);
      setAllergies([]);
      setOriginalMedications([]);
      setOriginalMedicalAllergies([]);
      setOriginalOtherAllergies([]);
      setEditableMedications([...dummyMedications]);
      setEditableMedicalAllergies([...dummyMedicalAllergies]);
      setEditableOtherAllergies([...dummyOtherAllergies]);
    } finally {
      setMedicalDataLoading(false);
    }
  };

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Handle edit button click
  const handleEditRecord = (section) => {
    setEditingSection(section);
    
    // Backup current data for cancel functionality
    let currentData = {};
    switch (section) {
      case 'general':
        currentData = { ...editablePatient };
        break;
      case 'medications':
        currentData = [...medications];
        setEditableMedications([...medications]);
        break;
      case 'medicalAllergies':
        const drugAllergies = allergies.filter(allergy => allergy.allergy_type === 'Drug');
        currentData = [...drugAllergies];
        setEditableMedicalAllergies([...drugAllergies]);
        break;
      case 'otherAllergies':
        const otherAllergies = allergies.filter(allergy => allergy.allergy_type !== 'Drug');
        currentData = [...otherAllergies];
        setEditableOtherAllergies([...otherAllergies]);
        break;
      default:
        break;
    }
    setBackupPatient(currentData);
  };

  // Handle save changes with proper database operations
  const handleSaveChanges = async (section) => {
    try {
      console.log(`💾 Saving changes for section: ${section}`);
      
      if (section === 'general') {
        setPatient(prev => ({
          ...prev,
          ...editablePatient
        }));
        setEditingSection(null);
        showToast('Changes saved successfully', 'success');
        return;
      }

      // Handle medical data sections
      if (section === 'medications') {
        await saveMedicationChanges();
      } else if (section === 'medicalAllergies') {
        await saveAllergyChanges('Drug', editableMedicalAllergies, originalMedicalAllergies);
      } else if (section === 'otherAllergies') {
        await saveAllergyChanges('Other', editableOtherAllergies, originalOtherAllergies);
      }

      // Clear inputs and exit editing mode
      setMedicationInput('');
      setMedicalAllergyInput('');
      setOtherAllergyInput('');
      setEditingSection(null);
      
      // Refresh data from API
      await fetchMedicalData(patient.id);
      
      showToast('Changes saved successfully', 'success');
    } catch (error) {
      console.error('❌ Error saving changes:', error);
      showToast('Failed to save changes', 'error');
    }
  };

  // Save medication changes
  const saveMedicationChanges = async () => {
    const originalIds = originalMedications.map(med => med.id);
    const editableIds = editableMedications.map(med => med.id).filter(id => id);

    // Find removed medications - mark as inactive instead of deleting
    const removedIds = originalIds.filter(id => !editableIds.includes(id));
    
    // Mark removed medications as inactive
    for (const medicationId of removedIds) {
      await patientMedicalService.updateMedication(medicationId, { status: 'Inactive' });
      console.log(`🔄 Marked medication ID ${medicationId} as Inactive`);
    }

    // Handle new medications (those without IDs)
    const newMedications = editableMedications.filter(med => !med.id);
    for (const medication of newMedications) {
      await patientMedicalService.createMedication({
        patient_id: patient.id,
        medication_name: medication.medication_name,
        dosage: medication.dosage,
        frequency: medication.frequency,
        start_date: new Date().toISOString().split('T')[0],
        status: 'Active'
      });
      console.log(`➕ Created new medication: ${medication.medication_name}`);
    }
  };

  // Save allergy changes
  const saveAllergyChanges = async (allergyType, editableAllergies, originalAllergies) => {
    const originalIds = originalAllergies.map(allergy => allergy.id);
    const editableIds = editableAllergies.map(allergy => allergy.id).filter(id => id);

    // Find removed allergies - mark as inactive instead of deleting
    const removedIds = originalIds.filter(id => !editableIds.includes(id));
    
    // Mark removed allergies as inactive
    for (const allergyId of removedIds) {
      await patientMedicalService.updateAllergy(allergyId, { status: 'Inactive' });
      console.log(`🔄 Marked allergy ID ${allergyId} as Inactive`);
    }

    // Handle new allergies (those without IDs)
    const newAllergies = editableAllergies.filter(allergy => !allergy.id);
    for (const allergy of newAllergies) {
      await patientMedicalService.createAllergy({
        patient_id: patient.id,
        allergen_name: allergy.allergen_name,
        allergy_type: allergyType === 'Drug' ? 'Drug' : (allergy.allergy_type || 'Other'),
        severity: allergy.severity || 'Mild',
        status: 'Active'
      });
      console.log(`➕ Created new allergy: ${allergy.allergen_name}`);
    }
  };

  // Handle cancel changes
  const handleCancelChanges = (section) => {
    setEditingSection(null);
    
    // Restore backup data
    switch (section) {
      case 'general':
        setEditablePatient(backupPatient);
        break;
      case 'medications':
        setEditableMedications([...backupPatient]);
        setMedicationInput('');
        break;
      case 'medicalAllergies':
        setEditableMedicalAllergies([...backupPatient]);
        setMedicalAllergyInput('');
        break;
      case 'otherAllergies':
        setEditableOtherAllergies([...backupPatient]);
        setOtherAllergyInput('');
        break;
      default:
        break;
    }
  };

  // Handle adding new tag
  const handleAddTag = (type, value) => {
    if (!value.trim()) return;
    
    switch (type) {
      case 'medications':
        // Parse medication input (e.g., "Aspirin 81mg daily")
        const parts = value.trim().split(' ');
        const medicationName = parts[0] || value.trim();
        const dosage = parts[1] || '';
        const frequency = parts.slice(2).join(' ') || '';
        
        const newMedication = {
          medication_name: medicationName,
          dosage: dosage,
          frequency: frequency,
          status: 'Active'
        };
        
        setEditableMedications(prev => [...prev, newMedication]);
        setMedicationInput('');
        break;
      case 'medicalAllergies':
        const newDrugAllergy = {
          allergen_name: value.trim(),
          allergy_type: 'Drug',
          severity: 'Mild',
          status: 'Active'
        };
        setEditableMedicalAllergies(prev => [...prev, newDrugAllergy]);
        setMedicalAllergyInput('');
        break;
      case 'otherAllergies':
        const newOtherAllergy = {
          allergen_name: value.trim(),
          allergy_type: 'Other',
          severity: 'Mild',
          status: 'Active'
        };
        setEditableOtherAllergies(prev => [...prev, newOtherAllergy]);
        setOtherAllergyInput('');
        break;
      default:
        break;
    }
  };

  // Handle removing tag
  const handleRemoveTag = (type, index) => {
    switch (type) {
      case 'medications':
        setEditableMedications(prev => prev.filter((_, i) => i !== index));
        break;
      case 'medicalAllergies':
        setEditableMedicalAllergies(prev => prev.filter((_, i) => i !== index));
        break;
      case 'otherAllergies':
        setEditableOtherAllergies(prev => prev.filter((_, i) => i !== index));
        break;
      default:
        break;
    }
  };

  // Handle key press for tag input
  const handleTagInputKeyPress = (e, type) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      switch (type) {
        case 'medications':
          handleAddTag('medications', medicationInput);
          break;
        case 'medicalAllergies':
          handleAddTag('medicalAllergies', medicalAllergyInput);
          break;
        case 'otherAllergies':
          handleAddTag('otherAllergies', otherAllergyInput);
          break;
        default:
          break;
      }
    }
  };

  // Function to render editable tags with add/remove functionality
  const renderEditableTags = (items, type, inputValue, setInputValue) => {
    const formatItemDisplay = (item, type) => {
      if (type === 'medications') {
        return `${item.medication_name || ''} ${item.dosage || ''} ${item.frequency || ''}`.trim();
      } else if (type === 'medicalAllergies' || type === 'otherAllergies') {
        const severityDisplay = item.severity ? ` (${item.severity})` : '';
        return `${item.allergen_name || ''}${severityDisplay}`;
      }
      return item.toString();
    };

    const getItemBgColor = (item, type) => {
      if (type === 'medicalAllergies' || type === 'otherAllergies') {
        const severity = item.severity;
        if (severity === 'Severe' || severity === 'Life-threatening') {
          return 'bg-red-100 text-red-800 border-red-200';
        } else if (severity === 'Moderate') {
          return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        }
      }
      return 'bg-blue-100 text-blue-800 border-blue-200';
    };

    return (
      <div className="w-full bg-white border border-gray-300 rounded-md min-h-[2.5rem] p-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
        <div className="flex flex-wrap gap-1 items-center">
          {items.map((item, index) => (
            <span
              key={index}
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border group ${getItemBgColor(item, type)}`}
            >
              {formatItemDisplay(item, type)}
              <button
                onClick={() => handleRemoveTag(type, index)}
                className="ml-1 w-3 h-3 rounded-full bg-red-200 hover:bg-red-300 flex items-center justify-center opacity-70 hover:opacity-100 transition-all"
                title="Remove"
              >
                <svg className="w-2 h-2 text-red-800" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </span>
          ))}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => handleTagInputKeyPress(e, type)}
            placeholder={items.length === 0 ? "Type and press Enter to add..." : "Add more..."}
            className="flex-1 min-w-[120px] outline-none text-sm placeholder-gray-400 bg-transparent"
          />
        </div>
      </div>
    );
  };

  // Handle input change for editable fields
  const handleInputChange = (field, value) => {
    setEditablePatient(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate BMI from height and weight
  const calculateBMI = (height, weight) => {
    if (!height || !weight) return '';
    
    // Extract numeric values from strings (remove units like 'cm', 'cms', 'kg', 'kgs')
    const heightNum = parseFloat(height.toString().replace(/[^\d.]/g, ''));
    const weightNum = parseFloat(weight.toString().replace(/[^\d.]/g, ''));
    
    // Validate inputs
    if (isNaN(heightNum) || isNaN(weightNum) || heightNum <= 0 || weightNum <= 0) {
      return '';
    }
    
    // Convert height from cm to meters
    const heightInMeters = heightNum / 100;
    
    // Calculate BMI = weight (kg) / height (m)²
    const bmi = weightNum / (heightInMeters * heightInMeters);
    
    // Return BMI rounded to 1 decimal place
    return bmi.toFixed(1);
  };

  // BMI categorization function with color codes and notes
  const getBMICategory = (bmi) => {
    const bmiValue = parseFloat(bmi);
    if (isNaN(bmiValue)) return null;

    if (bmiValue < 16.0) {
      return {
        category: "Severe Underweight",
        color: "red",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        borderColor: "border-red-200",
        dotColor: "bg-red-500",
        note: "Very high risk of malnutrition, poor physical performance, fatigue, and even serious health outcomes."
      };
    } else if (bmiValue >= 16.0 && bmiValue <= 16.9) {
      return {
        category: "Moderate Underweight",
        color: "orange",
        bgColor: "bg-orange-100",
        textColor: "text-orange-800",
        borderColor: "border-orange-200",
        dotColor: "bg-orange-500",
        note: "Still a significant health concern—may indicate nutrient deficiency or underlying illnesses."
      };
    } else if (bmiValue >= 17.0 && bmiValue <= 18.4) {
      return {
        category: "Mild Underweight",
        color: "orange",
        bgColor: "bg-orange-100",
        textColor: "text-orange-800",
        borderColor: "border-orange-200",
        dotColor: "bg-orange-500",
        note: "Borderline low and may benefit from nutritional monitoring."
      };
    } else if (bmiValue >= 18.5 && bmiValue <= 24.9) {
      return {
        category: "Normal / Healthy Weight",
        color: "green",
        bgColor: "bg-green-100",
        textColor: "text-green-800",
        borderColor: "border-green-200",
        dotColor: "bg-green-500",
        note: "Associated with the lowest overall health risk on a population level."
      };
    } else if (bmiValue >= 25.0 && bmiValue <= 29.9) {
      return {
        category: "Overweight (Pre‑obese)",
        color: "orange",
        bgColor: "bg-orange-100",
        textColor: "text-orange-800",
        borderColor: "border-orange-200",
        dotColor: "bg-orange-500",
        note: "Elevated risk of chronic diseases like type 2 diabetes, hypertension, and cardiovascular conditions."
      };
    } else if (bmiValue >= 30.0 && bmiValue <= 34.9) {
      return {
        category: "Obese (Class I)",
        color: "red",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        borderColor: "border-red-200",
        dotColor: "bg-red-500",
        note: "Moderate obesity; increased risk of multiple health issues—early lifestyle changes are strongly recommended."
      };
    } else if (bmiValue >= 35.0 && bmiValue <= 39.9) {
      return {
        category: "Obese (Class II)",
        color: "red",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        borderColor: "border-red-200",
        dotColor: "bg-red-500",
        note: "Severe obesity; substantially elevated health risks, often requiring more intensive management."
      };
    } else if (bmiValue >= 40.0) {
      return {
        category: "Obese (Class III / Severe Obesity)",
        color: "red",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        borderColor: "border-red-200",
        dotColor: "bg-red-500",
        note: "Very severe obesity; associated with the highest likelihood of serious comorbidities and typically needs clinical intervention."
      };
    }
    
    return null;
  };

  // Component to render tags for multiple values
  const renderTags = (items, maxDisplay = 2) => {
    if (!items || items.length === 0) return null;
    
    const displayItems = items.slice(0, maxDisplay);
    const remainingCount = items.length - maxDisplay;
    
    return (
      <div className="flex flex-wrap gap-1 p-2">
        {displayItems.map((item, index) => (
          <span
            key={index}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
          >
            {item}
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
            +{remainingCount} more
          </span>
        )}
      </div>
    );
  };



  if (loading) {
    return (
      <div className="flex-1 space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="flex-1 space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="text-center text-gray-500">
            {error || 'Patient not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General Details Panel */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <h4 className="font-semibold">General Details</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsGeneralDetailsExpanded(!isGeneralDetailsExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isGeneralDetailsExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isGeneralDetailsExpanded && (
        <div className="p-4">
          {/* Single row layout for Patient Height, Weight, BMI */}
          <div className="flex gap-3">
            {/* Patient Height */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Height</label>
              <div className="relative">
                <input
                  type="text"
                  value={editingSection === 'general' ? editablePatient.height : (patient.height || 'Patient Height')}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  readOnly={editingSection !== 'general'}
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 pr-12 ${
                    editingSection === 'general' ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <div className="h-full w-px bg-gray-300 mx-2"></div>
                  <span className="text-xs text-gray-500 font-medium pr-3">cms</span>
                </div>
              </div>
            </div>

            {/* Patient Weight */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Weight</label>
              <div className="relative">
                <input
                  type="text"
                  value={editingSection === 'general' ? editablePatient.weight : (patient.weight || 'Patient Weight')}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  readOnly={editingSection !== 'general'}
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 pr-12 ${
                    editingSection === 'general' ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <div className="h-full w-px bg-gray-300 mx-2"></div>
                  <span className="text-xs text-gray-500 font-medium pr-3">kgs</span>
                </div>
              </div>
            </div>

            {/* Patient BMI */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient BMI</label>
              <div className="space-y-2">
                <div className="relative">
                  <input
                    type="text"
                    value={calculateBMI(
                      editingSection === 'general' ? editablePatient.height : patient.height, 
                      editingSection === 'general' ? editablePatient.weight : patient.weight
                    ) || 'BMI will calculate automatically'}
                    readOnly
                    className="w-full px-3 py-1 bg-gray-50 border border-gray-300 rounded-md text-gray-900 pr-12"
                  />
                  {calculateBMI(
                    editingSection === 'general' ? editablePatient.height : patient.height, 
                    editingSection === 'general' ? editablePatient.weight : patient.weight
                  ) && (
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <div className="h-full w-px bg-gray-300 mx-2"></div>
                      <span className="text-xs text-gray-500 font-medium pr-3">kg/m²</span>
                    </div>
                  )}
                </div>
                
                {/* BMI Category Display */}
                {calculateBMI(
                  editingSection === 'general' ? editablePatient.height : patient.height, 
                  editingSection === 'general' ? editablePatient.weight : patient.weight
                ) && getBMICategory(calculateBMI(
                  editingSection === 'general' ? editablePatient.height : patient.height, 
                  editingSection === 'general' ? editablePatient.weight : patient.weight
                )) && (
                  <div className="relative">
                    <div 
                      className={`w-full px-3 py-2 rounded-md border text-sm ${getBMICategory(calculateBMI(
                        editingSection === 'general' ? editablePatient.height : patient.height, 
                        editingSection === 'general' ? editablePatient.weight : patient.weight
                      )).bgColor} ${getBMICategory(calculateBMI(
                        editingSection === 'general' ? editablePatient.height : patient.height, 
                        editingSection === 'general' ? editablePatient.weight : patient.weight
                      )).textColor} ${getBMICategory(calculateBMI(
                        editingSection === 'general' ? editablePatient.height : patient.height, 
                        editingSection === 'general' ? editablePatient.weight : patient.weight
                      )).borderColor}`}
                      onMouseEnter={() => setShowBMITooltip(true)}
                      onMouseLeave={() => setShowBMITooltip(false)}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${getBMICategory(calculateBMI(
                          editingSection === 'general' ? editablePatient.height : patient.height, 
                          editingSection === 'general' ? editablePatient.weight : patient.weight
                        )).dotColor}`}></div>
                        <span className="font-medium">
                          {getBMICategory(calculateBMI(
                            editingSection === 'general' ? editablePatient.height : patient.height, 
                            editingSection === 'general' ? editablePatient.weight : patient.weight
                          )).category}
                        </span>
                      </div>
                    </div>
                    {showBMITooltip && (
                      <div className="absolute bottom-full left-0 mb-2 w-80 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50">
                        <div className="font-semibold mb-1">
                          {getBMICategory(calculateBMI(
                            editingSection === 'general' ? editablePatient.height : patient.height, 
                            editingSection === 'general' ? editablePatient.weight : patient.weight
                          )).category}
                        </div>
                        <div className="text-gray-300 leading-relaxed">
                          {getBMICategory(calculateBMI(
                            editingSection === 'general' ? editablePatient.height : patient.height, 
                            editingSection === 'general' ? editablePatient.weight : patient.weight
                          )).note}
                        </div>
                        {/* Tooltip arrow */}
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-4 space-x-2">
            {editingSection !== 'general' ? (
              <button 
                onClick={() => handleEditRecord('general')}
                className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Record</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancelChanges('general')}
                  className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={() => handleSaveChanges('general')}
                  className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </button>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Medications Panel */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Pill className="h-5 w-5" />
              <h4 className="font-semibold">Medications</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsMedicationsExpanded(!isMedicationsExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isMedicationsExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isMedicationsExpanded && (
        <div className="p-4">
          {/* Single row layout for Medications */}
          <div className="flex gap-3">
            {/* Patient Medications */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Medications</label>
              {editingSection === 'medications' ? (
                renderEditableTags(editableMedications, 'medications', medicationInput, setMedicationInput)
              ) : medicalDataLoading ? (
                <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center justify-center">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm">Loading medications...</span>
                  </div>
                </div>
              ) : (
                <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center">
                  {medications.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 p-2">
                      {medications.map((med, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                        >
                          {med.medication_name} {med.dosage} - {med.frequency}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-sm p-2">No medications found</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end mt-4 space-x-2">
            {editingSection !== 'medications' ? (
              <button 
                onClick={() => handleEditRecord('medications')}
                className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Record</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancelChanges('medications')}
                  className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={() => handleSaveChanges('medications')}
                  className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </button>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Medical Allergies Panel */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5" />
              <h4 className="font-semibold">Medical Allergies</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsMedicalAllergiesExpanded(!isMedicalAllergiesExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isMedicalAllergiesExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isMedicalAllergiesExpanded && (
        <div className="p-4">
          {/* Single row layout for Medical Allergies */}
          <div className="flex gap-3">
            {/* Patient Medical Allergies */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Medical Allergies</label>
              {editingSection === 'medicalAllergies' ? (
                renderEditableTags(editableMedicalAllergies, 'medicalAllergies', medicalAllergyInput, setMedicalAllergyInput)
              ) : medicalDataLoading ? (
                <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center justify-center">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm">Loading allergies...</span>
                  </div>
                </div>
              ) : (
                <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center">
                  {allergies.filter(allergy => allergy.allergy_type === 'Drug').length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 p-2">
                      {allergies
                        .filter(allergy => allergy.allergy_type === 'Drug')
                        .map((allergy, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                              allergy.severity === 'Severe' || allergy.severity === 'Life-threatening'
                                ? 'bg-red-100 text-red-800 border-red-200'
                                : allergy.severity === 'Moderate'
                                ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                : 'bg-orange-100 text-orange-800 border-orange-200'
                            }`}
                          >
                            {allergy.allergen_name}
                            <span className="ml-1 text-xs">({allergy.severity})</span>
                          </span>
                        ))}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-sm p-2">No drug allergies found</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end mt-4 space-x-2">
            {editingSection !== 'medicalAllergies' ? (
              <button 
                onClick={() => handleEditRecord('medicalAllergies')}
                className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Record</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancelChanges('medicalAllergies')}
                  className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={() => handleSaveChanges('medicalAllergies')}
                  className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </button>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Other Allergies Panel */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <h4 className="font-semibold">Other Allergies</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsOtherAllergiesExpanded(!isOtherAllergiesExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isOtherAllergiesExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isOtherAllergiesExpanded && (
        <div className="p-4">
          {/* Single row layout for Other Allergies */}
          <div className="flex gap-3">
            {/* Patient Other Allergies */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Other Allergies</label>
              {editingSection === 'otherAllergies' ? (
                renderEditableTags(editableOtherAllergies, 'otherAllergies', otherAllergyInput, setOtherAllergyInput)
              ) : medicalDataLoading ? (
                <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center justify-center">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm">Loading allergies...</span>
                  </div>
                </div>
              ) : (
                <div className="w-full bg-gray-50 border border-gray-300 rounded-md min-h-[2.5rem] flex items-center">
                  {allergies.filter(allergy => allergy.allergy_type !== 'Drug').length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 p-2">
                      {allergies
                        .filter(allergy => allergy.allergy_type !== 'Drug')
                        .map((allergy, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                              allergy.severity === 'Severe' || allergy.severity === 'Life-threatening'
                                ? 'bg-red-100 text-red-800 border-red-200'
                                : allergy.severity === 'Moderate'
                                ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                : 'bg-green-100 text-green-800 border-green-200'
                            }`}
                          >
                            {allergy.allergen_name}
                            <span className="ml-1 text-xs">({allergy.allergy_type})</span>
                          </span>
                        ))}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-sm p-2">No other allergies found</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end mt-4 space-x-2">
            {editingSection !== 'otherAllergies' ? (
              <button 
                onClick={() => handleEditRecord('otherAllergies')}
                className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Record</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancelChanges('otherAllergies')}
                  className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={() => handleSaveChanges('otherAllergies')}
                  className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </button>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-md shadow-lg text-white ${
          toast.type === 'success' ? 'bg-green-500' : 
          toast.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default MedicalDetailsContent;
